import { useEffect, useRef } from 'react';
import type React from 'react';

import { screens } from '@change/ds-tokens/screens';

/**
 * Mirrors the DS Modal's own `max-md:` sheet breakpoint — below this it is a sheet, above a dialog.
 * Derived from the token rather than written out: Tailwind v4 compiles `max-md:` to `width < md`, so
 * a literal `max-width` has to be under the breakpoint, and a hardcoded one drifts if `md` moves.
 */
const SHEET_MEDIA_QUERY = `(width < ${screens.md}px)`;

/**
 * Returns a ref to attach to a node inside the modal's sheet. On mobile, opening the on-screen
 * keyboard shrinks `window.visualViewport` without shrinking `window.innerHeight` — the DS Modal
 * sheet is `position: fixed`, which iOS WebKit doesn't reposition against the shrunk visual
 * viewport, so the sheet (and whatever's focused inside it) can end up covered by the keyboard.
 * This nudges the sheet up by the keyboard's height so it stays visible above it.
 */
export function useKeepModalVisibleAboveKeyboard(): React.RefObject<HTMLDivElement | null> {
	const anchorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const { visualViewport } = window;
		if (!visualViewport) return undefined;

		// `scroll` fires continuously through a drag or pinch, so the pending frame is cancelled before
		// queueing a new one — otherwise a single gesture stacks one frame per event, each writing the
		// same transform.
		let rafId: number | undefined;

		const handleViewportChange = () => {
			// Modal doesn't forward a ref or style prop to its actual sheet panel (the animated
			// container with the sheet's background/rounded corners) — only to an outer wrapper div
			// that isn't part of that panel. This walks up from our own anchor node to find it instead.
			const sheet = anchorRef.current?.closest('[role="dialog"]')?.parentElement;
			if (!(sheet instanceof HTMLElement)) return;

			// Only the bottom sheet needs this. The DS switches to a centred dialog at `md`, where it is
			// already inside the visual viewport, and nudging it up pushes it off the top instead.
			const isSheet = window.matchMedia(SHEET_MEDIA_QUERY).matches;

			const keyboardHeight = window.innerHeight - visualViewport.height;
			const unwantedModalPaddingBottom = 32;
			// Clamped at 0: a keyboard shorter than the padding gives a negative offset, and
			// `translateY(-${-12}px)` is `translateY(--12px)` — invalid CSS the browser drops silently.
			const offset = Math.max(0, keyboardHeight - visualViewport.offsetTop - unwantedModalPaddingBottom);
			const transform = isSheet && keyboardHeight > 0 ? `translateY(-${offset}px)` : 'translateY(0)';

			if (rafId !== undefined) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(() => {
				rafId = undefined;
				sheet.style.transform = transform;
			});
		};

		visualViewport.addEventListener('resize', handleViewportChange);
		visualViewport.addEventListener('scroll', handleViewportChange);

		return () => {
			if (rafId !== undefined) cancelAnimationFrame(rafId);
			visualViewport.removeEventListener('resize', handleViewportChange);
			visualViewport.removeEventListener('scroll', handleViewportChange);
		};
	}, []);

	return anchorRef;
}
